import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import {
  MapPin,
  Briefcase,
  GraduationCap,
  FileText,
  Video,
  MessageSquare,
} from 'lucide-react';
import Avatar from '../StudentPlatform/shared/Avatar';

const StudentCandidateCard = ({ student, onClick, isMessaging }) => {
  const intl = useIntl();
  const displayName = student.user?.allowed_name || student.user?.name || intl.formatMessage({ id: 'hiring.card.student_fallback' });
  const avatarUrl = student.avatar_url;
  const isEmployeeCandidate = student.candidate_track === 'employee';

  return (
    <div
      onClick={() => onClick(student)}
      className="bg-succession-bg-primary border border-succession-border rounded-lg cursor-pointer hover:shadow-succession-lg transition-all duration-200 hover:-translate-y-0.5 hover:border-succession-primary group"
    >
      <div className="p-5">
        {/* Avatar and Name */}
        <div className="flex items-start gap-3 mb-2">
          <div className="relative flex-shrink-0">
            <Avatar
              src={avatarUrl}
              name={displayName}
              size="lg"
              className="ring-2 ring-succession-border group-hover:ring-succession-primary transition-colors"
            />
            {isMessaging && (
              <div className="absolute -top-1 -right-1 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center ring-2 ring-white">
                <MessageSquare className="w-3.5 h-3.5 text-white" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 style={{ margin: 0 }} className="font-semibold text-base leading-tight text-succession-text-primary truncate group-hover:text-succession-primary transition-colors">
              {displayName}
            </h3>
            <div className="flex items-center gap-2 mt-1">
              <p style={{ margin: 0 }} className="text-sm leading-tight text-succession-text-secondary truncate">
                {isEmployeeCandidate
                  ? (student.experience_level || intl.formatMessage({ id: 'hiring.card.not_specified' }))
                  : (student.school || intl.formatMessage({ id: 'hiring.card.school_not_specified' }))}
              </p>
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold ${
                isEmployeeCandidate ? 'bg-indigo-100 text-indigo-700' : 'bg-sky-100 text-sky-700'
              }`}>
                {isEmployeeCandidate ? 'Employee' : 'Student'}
              </span>
            </div>
          </div>
        </div>

        {/* Info Rows */}
        <div className="space-y-2 mb-4">
          {!isEmployeeCandidate && (
            <div className="flex items-start gap-2">
              <GraduationCap className="w-4 h-4 text-succession-text-muted mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-succession-text-secondary">{intl.formatMessage({ id: 'hiring.card.label.program' })}</p>
                <p className="text-sm font-medium text-succession-text-primary truncate">
                  {student.degree_program || intl.formatMessage({ id: 'hiring.card.not_specified' })}
                </p>
              </div>
            </div>
          )}

          {!isEmployeeCandidate && (
            <div className="flex items-start gap-2">
              <GraduationCap className="w-4 h-4 text-succession-text-muted mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-succession-text-secondary">{intl.formatMessage({ id: 'hiring.card.label.graduating' })}</p>
                <p className="text-sm font-medium text-succession-text-primary">
                  {student.graduation_year || intl.formatMessage({ id: 'hiring.card.not_specified' })}
                </p>
              </div>
            </div>
          )}

          {isEmployeeCandidate && (
            <div className="flex items-start gap-2">
              <GraduationCap className="w-4 h-4 text-succession-text-muted mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-succession-text-secondary">Experience Level</p>
                <p className="text-sm font-medium text-succession-text-primary truncate">
                  {student.experience_level || intl.formatMessage({ id: 'hiring.card.not_specified' })}
                </p>
              </div>
            </div>
          )}

          <div className="flex items-start gap-2">
            <MapPin className="w-4 h-4 text-succession-text-muted mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-succession-text-secondary">{intl.formatMessage({ id: 'hiring.card.label.location' })}</p>
              <p className="text-sm font-medium text-succession-text-primary truncate">
                {[student.city, student.state].filter(Boolean).join(', ') || intl.formatMessage({ id: 'hiring.card.not_specified' })}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-2">
            <Briefcase className="w-4 h-4 text-succession-text-muted mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-succession-text-secondary">{isEmployeeCandidate ? 'Interested In' : intl.formatMessage({ id: 'hiring.card.label.seeking' })}</p>
              {student.employment_type && !isEmployeeCandidate ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-succession-primary/10 text-succession-primary mt-0.5">
                  {student.employment_type === 'internship'
                    ? intl.formatMessage({ id: 'hiring.card.type.internship' })
                    : intl.formatMessage({ id: 'hiring.card.type.full_time' })}
                </span>
              ) : (student.candidate_interests && student.candidate_interests.length > 0 && isEmployeeCandidate) ? (
                <p className="text-sm font-medium text-succession-text-primary truncate">
                  {student.candidate_interests.slice(0, 2).join(', ')}
                </p>
              ) : (
                <p className="text-sm font-medium text-succession-text-primary">{intl.formatMessage({ id: 'hiring.card.not_specified' })}</p>
              )}
            </div>
          </div>
        </div>

        {/* Certifications */}
        {student.certifications && student.certifications.length > 0 && (
          <div className="mb-4">
            <p className="text-xs text-succession-text-secondary mb-2">{intl.formatMessage({ id: 'hiring.card.certifications' })}</p>
            <div className="flex flex-wrap gap-1.5">
              {student.certifications.slice(0, 3).map((cert, idx) => (
                <span
                  key={idx}
                  className="text-xs px-2 py-1 rounded border border-succession-border text-succession-text-secondary bg-succession-bg-secondary"
                >
                  {cert}
                </span>
              ))}
              {student.certifications.length > 3 && (
                <span className="text-xs px-2 py-1 rounded text-succession-text-muted">
                  +{student.certifications.length - 3}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="border-t border-succession-border pt-3 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <FileText
              className={`w-4 h-4 ${student.resume_url ? 'text-succession-primary' : 'text-succession-text-muted/40'}`}
            />
            <span
              className={`text-xs ${student.resume_url ? 'text-succession-text-primary font-medium' : 'text-succession-text-muted'}`}
            >
              {intl.formatMessage({ id: 'hiring.card.resume' })}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <Video
              className={`w-4 h-4 ${student.video_url ? 'text-succession-primary' : 'text-succession-text-muted/40'}`}
            />
            <span
              className={`text-xs ${student.video_url ? 'text-succession-text-primary font-medium' : 'text-succession-text-muted'}`}
            >
              {intl.formatMessage({ id: 'hiring.card.video' })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

StudentCandidateCard.propTypes = {
  student: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired,
  isMessaging: PropTypes.bool,
};

export default StudentCandidateCard;
